import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  IJourneyLeg,
  IJourneyLocation,
  IJourneyTrip,
  IOperatorMapping,
} from '@web/dtos';
import { OnDemandAvailabilityStore } from '../../../services/api/on-demand/on-demand-availability-store.service';
import { ReconRouteStore } from '../../../services/api/journey-planning/recon/recon-route-store.service';
import { OnDemandSelectionStateService } from '../../../services/on-demand/on-demand-selection-state.service';
import { formatSimpleTime } from '@web/shared-functions/date';

@Injectable({ providedIn: 'root' })
export class OnDemandJourneyViewModel {
  private readonly availabilityStore: OnDemandAvailabilityStore = inject(
    OnDemandAvailabilityStore
  );
  private readonly reconRouteStore: ReconRouteStore = inject(ReconRouteStore);
  private readonly selectionState: OnDemandSelectionStateService = inject(
    OnDemandSelectionStateService
  );

  readonly trip: Signal<IJourneyTrip | undefined> = computed(
    () =>
      this.reconRouteStore.reconRoute() ??
      this.selectionState.checkoutTrip() ??
      undefined
  );

  readonly operatorMapping: Signal<IOperatorMapping | undefined> = computed(
    () =>
      this.availabilityStore.matchedMapping() ??
      this.selectionState.checkoutOperatorMapping() ??
      undefined
  );

  readonly onDemandLeg: Signal<IJourneyLeg | undefined> = computed(
    () =>
      this.availabilityStore.onDemandLeg() ??
      this.trip()?.legs.find(
        leg => leg.operator.id === this.operatorMapping()?.operatorId
      )
  );

  readonly regularLegs: Signal<IJourneyLeg[]> = computed(
    () =>
      this.trip()?.legs.filter(
        leg =>
          leg.type === 'JNY' &&
          !!leg.operator.id &&
          leg.operator.id !== this.onDemandLeg()?.operator.id
      ) ?? []
  );

  readonly regularLeg: Signal<IJourneyLeg | undefined> = computed(
    () => this.regularLegs()[0]
  );

  readonly regularDestination: Signal<IJourneyLocation | undefined> = computed(
    () => {
      const regularLegs: IJourneyLeg[] = this.regularLegs();

      return regularLegs[regularLegs.length - 1]?.destination;
    }
  );

  readonly operatorName: Signal<string> = computed(
    () => this.operatorMapping()?.name ?? 'AST'
  );

  readonly tripDate: Signal<string | undefined> = computed(
    () => this.trip()?.origin?.date
  );

  readonly plannedDeparture: Signal<string | null> = computed(() => {
    const time = this.onDemandLeg()?.origin?.time;
    if (time) {
      return formatSimpleTime(time);
    }

    return null;
  });

  readonly plannedArrival: Signal<string | null> = computed(() => {
    const time = this.onDemandLeg()?.destination?.time;
    if (time) {
      return formatSimpleTime(time);
    }

    return null;
  });

  readonly hasDepartureDeviation: Signal<boolean> = computed(() => {
    const planned = this.plannedDeparture();
    const actual = this.selectionState.confirmedDeparture();
    return !!planned && !!actual && planned !== actual;
  });

  readonly hasArrivalDeviation: Signal<boolean> = computed(() => {
    if (!isLastJourneyLeg(this.trip(), this.onDemandLeg())) {
      return false;
    }

    const planned = this.plannedArrival();
    const actual = this.selectionState.confirmedArrival();
    return !!planned && !!actual && planned !== actual;
  });

  readonly hasAnyDeviation: Signal<boolean> = computed(
    () => this.hasDepartureDeviation() || this.hasArrivalDeviation()
  );

  isOnDemandLeg(operatorId: string): boolean {
    return this.onDemandLeg()?.operator.id === operatorId;
  }
}

export function isLastJourneyLeg(
  trip: IJourneyTrip | undefined,
  leg: IJourneyLeg | undefined
): boolean {
  const lastLeg = trip?.legs[trip.legs.length - 1];
  if (!lastLeg || !leg) {
    return false;
  }

  if (lastLeg === leg) {
    return true;
  }

  return lastLeg.id === leg.id;
}
